import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Pause, Play, Eye } from 'lucide-react';
import './DevUpdateControls.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only controls for the 7 AM / 9 PM daily development update email.
 *
 *   ▸ Shows the current pause state (paused until date, or "active")
 *   ▸ Pause N days (drop-down: 3 / 7 / 14 / 30)
 *   ▸ Unpause immediately
 *   ▸ "Preview next run" — calls /api/dev-updates/preview and reveals whether
 *     the scheduler would SEND or SKIP on the next scheduled firing, plus the
 *     human-readable reason + entry count if it would send.
 *
 * Designed to be tucked into the AdminDashboard Quick Actions area. All
 * endpoints return quickly so we don't need spinners beyond a simple
 * "Working..." label.
 */
const DevUpdateControls = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null); // { paused, paused_until }
  const [preview, setPreview] = useState(null); // compute_scheduled_decision shape
  const [pauseDays, setPauseDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null); // {type, msg}

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/dev-updates/pause-status`, { headers: authHeaders });
      if (res.ok) setStatus(await res.json());
    } catch {
      /* swallow */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const flashMsg = (type, msg) => {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 3500);
  };

  const handlePause = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${API_URL}/api/dev-updates/pause?days=${pauseDays}`,
        { method: 'POST', headers: authHeaders }
      );
      if (res.ok) {
        await fetchStatus();
        flashMsg('ok', `Paused for ${pauseDays} days`);
      } else {
        flashMsg('err', 'Failed to pause');
      }
    } catch {
      flashMsg('err', 'Failed to pause');
    } finally {
      setBusy(false);
    }
  };

  const handleUnpause = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/dev-updates/unpause`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (res.ok) {
        await fetchStatus();
        flashMsg('ok', 'Resumed');
      } else {
        flashMsg('err', 'Failed to resume');
      }
    } catch {
      flashMsg('err', 'Failed to resume');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const res = await fetch(`${API_URL}/api/dev-updates/preview`, { headers: authHeaders });
      if (res.ok) {
        setPreview(await res.json());
      } else {
        flashMsg('err', 'Preview unavailable');
      }
    } catch {
      flashMsg('err', 'Preview unavailable');
    } finally {
      setBusy(false);
    }
  };

  const pausedUntilFriendly = status?.paused_until
    ? new Date(status.paused_until).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="dev-update-controls" data-testid="dev-update-controls">
      <div className="duc-header">
        <span className="duc-title">Daily Update Email</span>
        <span
          className={`duc-state ${status?.paused ? 'duc-state-paused' : 'duc-state-active'}`}
          data-testid="duc-state-label"
        >
          {status === null
            ? '…'
            : status.paused
            ? `Paused until ${pausedUntilFriendly}`
            : 'Active (7 AM & 9 PM EST)'}
        </span>
      </div>

      <div className="duc-row">
        {!status?.paused ? (
          <>
            <label className="duc-label" htmlFor="duc-days">
              Mute for
            </label>
            <select
              id="duc-days"
              className="duc-select"
              value={pauseDays}
              onChange={(e) => setPauseDays(Number(e.target.value))}
              disabled={busy}
              data-testid="duc-days-select"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
            <button
              type="button"
              className="duc-btn duc-btn-pause"
              onClick={handlePause}
              disabled={busy}
              data-testid="duc-pause-btn"
            >
              <Pause size={14} />
              <span>Pause</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="duc-btn duc-btn-resume"
            onClick={handleUnpause}
            disabled={busy}
            data-testid="duc-unpause-btn"
          >
            <Play size={14} />
            <span>Resume now</span>
          </button>
        )}
        <button
          type="button"
          className="duc-btn duc-btn-preview"
          onClick={handlePreview}
          disabled={busy}
          data-testid="duc-preview-btn"
        >
          <Eye size={14} />
          <span>Preview next run</span>
        </button>
      </div>

      {preview && (
        <div
          className={`duc-preview ${preview.would_send ? 'duc-preview-send' : 'duc-preview-skip'}`}
          data-testid="duc-preview-result"
        >
          <div className="duc-preview-line">
            {preview.would_send ? '✓ Would send' : '✗ Would skip'}
            <span className="duc-preview-reason">{preview.reason}</span>
          </div>
          {preview.would_send && preview.entries?.length > 0 && (
            <div className="duc-preview-entries">
              <div className="duc-preview-entries-label">
                {preview.entries.length} entries ready:
              </div>
              <ul>
                {preview.entries.slice(0, 5).map((entry, idx) => (
                  <li key={idx}>{entry.category}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {flash && (
        <div
          className={`duc-flash duc-flash-${flash.type}`}
          role="status"
          data-testid="duc-flash"
        >
          {flash.msg}
        </div>
      )}
    </div>
  );
};

export default DevUpdateControls;
